import {
  ProductMappingSettings,
  SearchProduct,
  SearchProductMetaData,
} from '@epicuro-next/models/products';
import { DescriptionAttribute, Product } from 'graphql-api/gql-api-interfaces';

import {
  getProductAttribute,
  getProductAttributeColor,
  getProductAttributeColorCode,
} from './get-product-attribute';
import { mapProductLabels } from './map-product-labels';
import { mapProductMedia } from './map-product-media';
import { mapProductPrices } from './map-product-prices';

export function mapProductV2(
  product: SearchProduct,
  settings: ProductMappingSettings = {},
  descriptionAttributes: DescriptionAttribute[] = [],
): Product {
  const { brandId, productId, description } = product;
  const parent = product.masterProduct
    ? mapProductV2(product.masterProduct)
    : undefined;
  const media = mapProductMedia(product);
  const articleMedia = mapProductMedia(product.masterProduct);

  return {
    parent,
    displayedProductId: getDisplayedVariantProductId(
      product.productId,
      product.attributes,
      settings?.productAttributeToDisplayProductId,
    ),
    articleTitle: product.masterProduct?.title || '',
    articleImageUrl: articleMedia.length > 0 ? articleMedia[0].url : '',
    title: product.title ?? '',
    productId,
    brandId,
    description,
    descriptionAttributes,
    mainImageUrl: media.length > 0 ? media[0].url : undefined,
    attributes: product.attributes || {},
    masterAttributes: {},
    media,
    priceRanges: mapProductPrices(product),
    color: getProductAttributeColor(product.attributes),
    colorCode: getProductAttributeColorCode(product.attributes),
    parentId: parent?.productId,
    earliestDeliveryDate: getMetaDataValue<string>(
      product,
      SearchProductMetaData.EarliestAvailableDate,
    ),
    isNew: getMetaDataValue<boolean>(
      product,
      SearchProductMetaData.IsNewProduct,
    ),
    isAvailable: getMetaDataValue<boolean>(
      product,
      SearchProductMetaData.IsAvailable,
    ),
    popularity:
      getMetaDataValue<number>(product, SearchProductMetaData.Popularity) || 0,
    labels: mapProductLabels(product.labels ?? []),
  } as Product;
}

function getDisplayedVariantProductId(
  productId: string,
  attributes?: { [key: string]: string[] },
  attributeToUse?: string,
) {
  return attributeToUse
    ? getProductAttribute([attributeToUse], attributes) ?? productId
    : productId;
}

function getMetaDataValue<T extends string | number | boolean>(
  product: SearchProduct,
  key: string,
) {
  if (product.metaData) {
    return product.metaData[key] as T;
  }

  return undefined;
}
